import React, { useState, useEffect } from 'react';
import {
  Box, Paper, TextField, InputAdornment,
  Fab, List, ListItem, ListItemText, ListItemAvatar,
  Avatar, IconButton, Typography
} from '@mui/material';
import { Search, Add, Phone, Email, Edit, PersonOff } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../../api/axios';
import LoadingState from '../shared/LoadingState';
import EmptyState from '../shared/EmptyState';

const ClientsScreen = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState('');

  useEffect(() => {
    loadClients();
  }, []);

  const loadClients = async () => {
    setLoading(true);
    try {
      // Fetch all clients - in a real app might want pagination
      const response = await api.get('/admin/clients');
      setClients(response.data || []);
    } catch (err) {
      console.error('Failed to load clients:', err);
    }
    setLoading(false);
  };

  const filteredClients = clients.filter(client => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      client.name?.toLowerCase().includes(searchLower) ||
      client.email?.toLowerCase().includes(searchLower) ||
      client.phone_number?.toLowerCase().includes(searchLower)
    );
  });

  const getEmptyStateProps = () => {
    if (search) {
      return {
        icon: <PersonOff />,
        title: 'No results found',
        description: `No clients found matching "${search}"`,
        actionLabel: 'Clear Search',
        onAction: () => setSearch('')
      };
    }
    return {
      icon: <PersonOff />,
      title: 'No clients yet',
      description: 'Add your first client to get started',
      actionLabel: 'Add Client',
      onAction: () => navigate('/clients/new')
    };
  };

  return (
    <Box sx={{ pb: 10 }}>
      {/* Search */}
      <Paper 
        elevation={0} 
        sx={{ 
            p: 2, 
            mb: 3, 
            border: '1px solid #E2E8F0', 
            borderRadius: 3,
            bgcolor: 'white'
        }}
      >
        <TextField
          fullWidth
          size="small"
          placeholder="Search clients by name, email or phone..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search color="action" />
              </InputAdornment>
            ),
            sx: { borderRadius: 2 }
          }}
          sx={{ 
            '& .MuiOutlinedInput-root': {
                bgcolor: '#F8FAFC',
                '& fieldset': { borderColor: '#E2E8F0' },
                '&:hover fieldset': { borderColor: '#CBD5E1' }
            }
          }}
        />
      </Paper>

      {/* Clients List */}
      <Box>
        {loading ? (
          <LoadingState message="Loading clients..." />
        ) : filteredClients.length === 0 ? (
          <Paper 
            elevation={0}
            sx={{ 
                borderRadius: 3,
                bgcolor: '#F8FAFC',
                border: '1px dashed #E2E8F0',
                overflow: 'hidden'
            }}
          >
            <EmptyState {...getEmptyStateProps()} />
          </Paper>
        ) : (
          <Paper elevation={0} sx={{ border: '1px solid #E2E8F0', borderRadius: 3, overflow: 'hidden' }}>
            <List sx={{ p: 0 }}>
              {filteredClients.map((client, index) => (
                <React.Fragment key={client.id}>
                  <ListItem 
                    button 
                    onClick={() => navigate(`/clients/${client.id}`)}
                    alignItems="flex-start"
                    secondaryAction={
                      <IconButton edge="end" onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/clients/${client.id}/edit`);
                      }}>
                        <Edit fontSize="small" />
                      </IconButton>
                    }
                    sx={{ 
                      '&:hover': { bgcolor: '#F8FAFC' },
                      py: 2
                    }}
                  >
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: 'primary.light', color: 'white' }}>
                        {client.name?.charAt(0).toUpperCase()}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Typography variant="subtitle1" fontWeight={600}>
                            {client.name}
                          </Typography>
                        </Box>
                      }
                      secondary={
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          {client.email && (
                            <Box sx={{ display: 'flex', alignItems: 'center', fontSize: '0.875rem' }}>
                              <Email sx={{ fontSize: 16, mr: 1, opacity: 0.7 }} />
                              {client.email}
                            </Box>
                          )}
                          {client.phone_number && (
                            <Box sx={{ display: 'flex', alignItems: 'center', fontSize: '0.875rem' }}>
                              <Phone sx={{ fontSize: 16, mr: 1, opacity: 0.7 }} />
                              {client.phone_number}
                            </Box>
                          )}
                        </Box>
                      }
                    />
                  </ListItem>
                  {index < filteredClients.length - 1 && <React.Fragment><Box sx={{ borderBottom: '1px solid #F1F5F9', mx: 2 }} /></React.Fragment>}
                </React.Fragment>
              ))}
            </List>
          </Paper>
        )}
      </Box>

      {/* FAB for new client */}
      <Fab
        color="primary"
        sx={{ 
            position: 'fixed', 
            bottom: 80, 
            right: 24,
            boxShadow: '0 10px 15px -3px rgba(37, 99, 235, 0.4)'
        }}
        onClick={() => navigate('/clients/new')}
      >
        <Add />
      </Fab>
    </Box>
  );
};

export default ClientsScreen;
